import AbstractProvider from '../App/AbstractProvider';
import {IAuth} from '@src/@interfaces/services/Auth/IAuth';
import {fetchUserModel, setPreferences, updatePreference} from '@src/_redux/user/actions';
import {IDispatcher, IEventDefaultPayload, ISubscription} from '@src/@interfaces/services/Events';
import {HttpEvents} from '@src/@types/services/Http/Http';
import {IStoreEvents} from '@src/@interfaces/services/StoreEvents/IStoreEvents';
import {USER_CLEAR_REQUISITES, USER_SET_ACCESS_TOKEN, USER_SET_USER_MODEL} from '@src/_redux/user/constants';
import {UserPreferenceEvents} from '@src/@types/services/Auth/UserPreferenceEvents';
import {IUserPreference} from '@src/@interfaces/services/Auth/IUserPreference';
import {DateTime} from '@src/@types/DateTime';
import {
    finishWorkday,
    finishWorkdayBreak,
    startWorkday,
    startWorkdayBreak,
    startWorkdayLunchBreak
} from '@src/_redux/workday';

export default class AuthProvider extends AbstractProvider {
    readonly priority = 1;

    async boot(): Promise<void> {
        this.registerEventsListeners();
        this.subscribeToStoreEvents();
        this.watchForWorkdayEvents();
    }

    async loading(): Promise<void> {
        if (this.service.getAccessToken()) {
            this.app.dispatchAction(fetchUserModel());
        }
    }

    protected registerEventsListeners() {
        const dispatcher = this.getStoredService<IDispatcher>('events');

        dispatcher.listen<any>(HttpEvents.BEFORE_SEND_REQUEST, (event, payload) => {
            const token = this.service.getAccessToken();
            if (!token || !('options' in payload)) {
                return;
            }

            payload.options.credentials = payload.options?.credentials ?? 'include';
            payload.options.headers.set('Authorization', `Bearer ${token}`);
        });

        this.service.onUserDetailsChanged((event, payload) => {
            const list = payload.user?.preferences?.reduce((res, item) => {
                res[item.key] = {
                    id: item.id as number,
                    key: item.key,
                    value: item.value,
                };

                return res;
            }, {} as Parameters<typeof setPreferences>[0]);

            if (list) {
                this.app.dispatchAction(setPreferences(list));
            }
        });

        dispatcher.listen(UserPreferenceEvents.BEFORE_SAVE, (event, {preference}: { preference: IUserPreference }) => {
            this.app.dispatchAction(updatePreference(
                preference.getAttribute('key'),
                preference.getAttribute('value'),
            ));
        });
    }

    protected subscribeToStoreEvents() {
        const dispatcher = this.getStoredService<IStoreEvents>('store-events');

        dispatcher.listen(USER_SET_ACCESS_TOKEN, (event, payload) => {
            this.service.setAccessToken(payload.action.payload);
        });

        dispatcher.listen(USER_SET_USER_MODEL, (event, payload) => {
            this.service.setUserByAttributes(payload.action.payload);
        });

        dispatcher.listen(USER_CLEAR_REQUISITES, () => {
            // noinspection JSIgnoredPromiseFromCall
            this.service.logout();
        });
    }

    protected watchForWorkdayEvents() {
        let subscription: ISubscription | null = null;
        let previousUserId: string | number | null = null;
        this.service.onUserDetailsChanged((event, {user}) => {
            const userId = user?.id ?? null;
            const userChanged = userId !== previousUserId;

            if (subscription && userChanged) {
                subscription.unsubscribe();
                if (previousUserId) {
                    this.app.make('ws').leave(`model.user.${previousUserId}.time-counters`);
                }
            }

            previousUserId = userId;

            if (!userId) {
                return;
            }

            subscription = this.app.make('ws').onPrivateMessage<IEventDefaultPayload & {
                countableType: 'User' | string | null,
                type: 'user-workday@break' | 'user-workday@lunch-break',
                finished_at: DateTime | null,
                started_at: DateTime | null,
            }>(
                `model.user.${userId}.time-counters`,
                ['*'],
                (event, payload) => {
                    if (payload.countableType !== 'User') {
                        return;
                    }

                    const state = this.app.getStoreState()?.workday;
                    const isBreak = payload.model === 'TimeCounterBreak';
                    if (isBreak) {
                        if (payload.finished_at == null && !state?.currentBreak) {
                            this.app.dispatchAction(
                                payload.type === 'user-workday@lunch-break' ?
                                    startWorkdayLunchBreak() :
                                    startWorkdayBreak()
                            );
                        } else if (payload.finished_at != null && state?.currentBreak != null) {
                            this.app.dispatchAction(finishWorkdayBreak());
                        }

                        return;
                    }

                    if (payload.finished_at == null && !state?.started_at) {
                        this.app.dispatchAction(startWorkday(payload.started_at ?? undefined));
                    } else if (payload.finished_at != null && !state?.finished_at) {
                        this.app.dispatchAction(finishWorkday(payload.finished_at));
                    }
                }
            );
        });
    }

    protected get service() {
        return this.getStoredService<IAuth>('auth');
    }
}
